import type {
	CandidateWhatsAppMessageData,
	ConversationWindowData,
	WhatsAppMessageStatus,
	WhatsAppTemplateData,
} from '@jobilla/nexus-sdk'

export interface WhatsAppReadBy {
	userId: string
	readAt: Date
}

export interface WhatsAppMediaUrl {
	url: string
	content_type: string
}

export interface WhatsAppMessage {
	id: string
	direction: 'inbound' | 'outbound'
	status: WhatsAppMessageStatus
	content: string
	mediaUrls: WhatsAppMediaUrl[] | null
	templateName: string | null
	templateParameters: string[] | null
	sentAt: Date | null
	deliveredAt: Date | null
	readAt: Date | null
	createdAt: Date
	isRead: boolean
	isFromCandidate: boolean
	errorCode: string | null
	errorMessage: string | null
	readBy: WhatsAppReadBy[]
	isAutomated: boolean
}

export interface WhatsAppTemplate {
	id: string
	name: string
	language: string
	category: string
	status: string
	parameterCount: number
	parameterExamples: string[] | null
	parameterMap: Record<string, string> | null
	bodyText: string
}

export interface ConversationWindow {
	isOpen: boolean
	expiresAt: Date | null
	lastInboundAt: Date | null
}

export function mapApiConversationWindow(apiWindow: ConversationWindowData): ConversationWindow {
	return {
		isOpen: apiWindow.isOpen,
		expiresAt: apiWindow.expiresAt ? new Date(apiWindow.expiresAt) : null,
		lastInboundAt: apiWindow.lastInboundAt ? new Date(apiWindow.lastInboundAt) : null,
	}
}

export function mapApiMessageToWhatsAppMessage(
	apiMessage: CandidateWhatsAppMessageData,
	candidateId: string,
): WhatsAppMessage {
	const isFromCandidate = apiMessage.direction === 'inbound'
	const rawMediaUrls = apiMessage.mediaUrls

	// Transform media URLs to use our proxy endpoint (Meta media URLs require auth)
	// URL is relative to the API base URL (which includes /v1)
	const mediaUrls: WhatsAppMediaUrl[] | null = rawMediaUrls
		? rawMediaUrls.map((media, index) => ({
			url: `candidates/${candidateId}/whatsapp/${apiMessage.id}/media/${index}`,
			content_type: media.content_type,
		}))
		: null

	return {
		id: apiMessage.id,
		direction: apiMessage.direction as 'inbound' | 'outbound',
		status: apiMessage.status,
		content: apiMessage.content,
		mediaUrls,
		templateName: apiMessage.templateName,
		templateParameters: apiMessage.templateParameters,
		sentAt: apiMessage.sentAt ? new Date(apiMessage.sentAt) : null,
		deliveredAt: apiMessage.deliveredAt ? new Date(apiMessage.deliveredAt) : null,
		readAt: apiMessage.readAt ? new Date(apiMessage.readAt) : null,
		createdAt: new Date(apiMessage.createdAt),
		isRead: apiMessage.isRead,
		isFromCandidate,
		errorCode: apiMessage.errorCode,
		errorMessage: apiMessage.errorMessage,
		readBy: (Array.isArray(apiMessage.readBy) ? apiMessage.readBy : [])
			.filter((read) => read.readAt !== null)
			.map((read) => ({
				userId: read.userId,
				readAt: new Date(read.readAt),
			})),
		isAutomated: Boolean(apiMessage.isAutomated),
	}
}

export function mapApiTemplateToWhatsAppTemplate(apiTemplate: WhatsAppTemplateData): WhatsAppTemplate {
	// Extract body text from components - components is stored as an array of JSON strings
	let bodyText = ''
	for (const componentJson of apiTemplate.components) {
		try {
			const component = typeof componentJson === 'string' ? JSON.parse(componentJson) : componentJson
			if (component.type === 'BODY' && component.text) {
				bodyText = component.text
				break
			}
		} catch {
			// Skip invalid JSON
		}
	}

	return {
		id: apiTemplate.id,
		name: apiTemplate.name,
		language: apiTemplate.language,
		category: apiTemplate.category,
		status: apiTemplate.status,
		parameterCount: apiTemplate.parameterCount,
		parameterExamples: apiTemplate.parameterExamples,
		parameterMap: apiTemplate.parameterMap,
		bodyText,
	}
}
